import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Chips } from 'primereact/chips';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import type { Customer } from '../../types/customer';

interface ValidationState {
  primaryEmail: string;
  secondaryEmail: string[];
  customerNr: string;
  organization: string;
  company: string;
  company2: string[];
  country: string;
  language: string;
  telephone: string[];
  firstName: string;
  surName: string;
  valid: boolean;
}

interface CustomerFormProps {
  visible: boolean;
  customer: Customer;
  validationState: ValidationState;
  isAdmin: boolean;
  onHide: () => void;
  onSave: () => void;
  onInputChange: (e: any, name: string) => void;
  onInputBooleanChange: (e: any, name: string) => void;
  onSecondaryEmailAdd: (e: any, customer: Customer) => void;
  onSecondaryEmailRemove: (value: string[]) => void;
  onCompany2Add: (e: any) => void;
  onCompany2Remove: (e: any) => void;
  onTelephoneAdd: (e: any) => void;
  onTelephoneRemove: (e: any) => void;
  onValidateCustomerNr: (customerNr: string) => void;
  onValidatePrimaryEmail: (customer: Customer) => void;
  onValidateCompany: (company: string) => void;
  onValidateCountry: (country: string) => void;
  onValidateLanguage: (language: string) => void;
  onValidateFirstName: (firstName: string) => void;
  onValidateSurName: (surName: string) => void;
  onValidateOrganization: (organization: string) => void;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  visible,
  customer,
  validationState,
  isAdmin,
  onHide,
  onSave,
  onInputChange,
  onInputBooleanChange,
  onSecondaryEmailAdd,
  onSecondaryEmailRemove,
  onCompany2Add,
  onCompany2Remove,
  onTelephoneAdd,
  onTelephoneRemove,
  onValidateCustomerNr,
  onValidatePrimaryEmail,
  onValidateCompany,
  onValidateCountry,
  onValidateLanguage,
  onValidateFirstName,
  onValidateSurName,
  onValidateOrganization,
}) => {
  // Country options with flags
  const countries = [
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'NO', name: 'Norway' },
    { code: 'FI', name: 'Finland' },
  ];

  const countryOptionTemplate = (option: any) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <img 
          src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
          alt={`${option.code} flag`}
          style={{ 
            borderRadius: '2px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        />
        <span>{option.name}</span>
      </div>
    );
  };

  const selectedCountryTemplate = (option: any) => {
    if (!option) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <img 
          src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
          alt={`${option.code} flag`}
          style={{ 
            borderRadius: '2px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
        />
        <span>{option.name}</span>
      </div>
    );
  };

  // Language options with colored badges
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'pl', name: 'Polish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'da', name: 'Danish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'fi', name: 'Finnish' },
  ];

  const languageColors: { [key: string]: string } = {
    'en': '#4A90E2', 'de': '#E24A4A', 'fr': '#5BC0DE', 'es': '#F0AD4E',
    'it': '#5CB85C', 'nl': '#FF8C00', 'pt': '#9B59B6', 'pl': '#E74C3C',
    'sv': '#3498DB', 'da': '#E67E22', 'no': '#1ABC9C', 'fi': '#34495E',
  };

  const languageOptionTemplate = (option: any) => {
    const bgColor = languageColors[option.code] || '#6c757d';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '0.15rem 0.4rem',
            backgroundColor: bgColor,
            color: 'white',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            minWidth: '2rem',
            textAlign: 'center'
          }}
        >
          {option.code}
        </span>
        <span>{option.name}</span>
      </div>
    );
  };

  const selectedLanguageTemplate = (option: any) => {
    if (!option) return null;
    const bgColor = languageColors[option.code] || '#6c757d';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '0.15rem 0.4rem',
            backgroundColor: bgColor,
            color: 'white',
            borderRadius: '3px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            minWidth: '2rem',
            textAlign: 'center'
          }}
        >
          {option.code}
        </span>
        <span>{option.name}</span>
      </div>
    );
  };

  const customerDialogFooter = (
    <React.Fragment>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} />
      <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={onSave} />
    </React.Fragment>
  );

  return (
    <Dialog
      visible={visible}
      className="p-fluid"
      style={{ width: '50vw', minWidth: '600px' }}
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="pi pi-user" style={{ fontSize: '1.25rem', color: '#4A90E2' }}></i>
          <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Customer Details</span>
        </div>
      }
      modal
      footer={customerDialogFooter}
      onHide={onHide}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Basic Information Section */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <h6 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Basic Information
          </h6>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="field mb-4">
              <label htmlFor="customerNr" className="block mb-2 font-semibold text-900">
                Customer Nr. <span className="text-red-500">*</span>
              </label>
              <InputText
                id="customerNr"
                value={customer.customerNr}
                onKeyUp={() => onValidateCustomerNr(customer.customerNr)}
                onChange={(e) => onInputChange(e, 'customerNr')}
                required
                className={classNames('w-full', { 'p-invalid': validationState.customerNr })}
              />
              {validationState.customerNr && <small className="p-error">{validationState.customerNr}</small>}
            </div>

            {isAdmin && (
              <div className="field mb-4">
                <label htmlFor="organization" className="block mb-2 font-semibold text-900">
                  Organization <span className="text-red-500">*</span>
                </label>
                <InputText
                  id="organization"
                  value={customer.organization}
                  onKeyUp={() => onValidateOrganization(customer.organization)}
                  onChange={(e) => onInputChange(e, 'organization')}
                  required
                  className={classNames('w-full', { 'p-invalid': validationState.organization })}
                />
                {validationState.organization && <small className="p-error">{validationState.organization}</small>}
              </div>
            )}
          </div>
        </div>

        {/* Personal Information Section */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <h6 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Personal Information
          </h6>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="field mb-4">
              <label htmlFor="firstName" className="block mb-2 font-semibold text-900">
                First Name
              </label>
              <InputText
                id="firstName"
                value={customer.firstName}
                onKeyUp={() => onValidateFirstName(customer.firstName)}
                onChange={(e) => onInputChange(e, 'firstName')}
                className={classNames('w-full', { 'p-invalid': validationState.firstName })}
              />
              {validationState.firstName && <small className="p-error">{validationState.firstName}</small>}
            </div>

            <div className="field mb-4">
              <label htmlFor="surName" className="block mb-2 font-semibold text-900">
                Last Name <span className="text-red-500">*</span>
              </label>
              <InputText
                id="surName"
                value={customer.surName}
                onKeyUp={() => onValidateSurName(customer.surName)}
                onChange={(e) => onInputChange(e, 'surName')}
                required
                className={classNames('w-full', { 'p-invalid': validationState.surName })}
              />
              {validationState.surName && <small className="p-error">{validationState.surName}</small>}
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <h6 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Contact Information
          </h6>
          
          <div className="field mb-4">
            <label htmlFor="primaryEmail" className="block mb-2 font-semibold text-900">
              Primary Email <span className="text-red-500">*</span>
            </label>
            <InputText
              id="primaryEmail"
              value={customer.primaryEmail}
              onKeyUp={() => onValidatePrimaryEmail(customer)}
              onChange={(e) => onInputChange(e, 'primaryEmail')}
              required
              className={classNames('w-full', { 'p-invalid': validationState.primaryEmail })}
              placeholder="email@example.com"
            />
            {validationState.primaryEmail && <small className="p-error">{validationState.primaryEmail}</small>}
          </div>

          <div className="field mb-4">
            <label htmlFor="secondaryEmail" className="block mb-2 font-semibold text-900">
              Secondary Emails
            </label>
            <Chips
              id="secondaryEmail"
              addOnBlur={true}
              allowDuplicate={false}
              pt={{ removeTokenIcon: { onMouseDown: (e: any) => e.preventDefault() } }}
              value={(() => {
                // Handle secondaryEmail - filter out empty strings and "[]"
                if (Array.isArray(customer.secondaryEmail)) {
                  return customer.secondaryEmail.filter(email => email && email.trim() !== '' && email !== '[]');
                }
                if (customer.secondaryEmail && customer.secondaryEmail.trim() !== '' && customer.secondaryEmail !== '[]') {
                  return [customer.secondaryEmail];
                }
                return [];
              })()}
              onAdd={(e) => onSecondaryEmailAdd(e, customer)}
              onRemove={(e) => onSecondaryEmailRemove(Array.isArray(e.value) ? e.value : [e.value])}
              onChange={(e) => onInputChange(e, 'secondaryEmail')}
              separator=","
              className={classNames('w-full', { 'p-invalid': validationState.secondaryEmail.length > 0 })}
              placeholder="Press Enter to add email"
            />
            {validationState.secondaryEmail.length > 0 && (
              <small className="p-error">{validationState.secondaryEmail.toString()}</small>
            )}
          </div>

          <div className="field mb-4">
            <label htmlFor="telephone" className="block mb-2 font-semibold text-900">
              Phone Numbers <span className="text-red-500">*</span>
            </label>
            <Chips
              id="telephone"
              addOnBlur={true}
              allowDuplicate={false}
              pt={{ removeTokenIcon: { onMouseDown: (e: any) => e.preventDefault() } }}
              value={customer.telephone}
              onAdd={(e) => onTelephoneAdd(e)}
              onRemove={(e) => onTelephoneRemove(e)}
              onChange={(e) => onInputChange(e, 'telephone')}
              className={classNames('w-full', { 'p-invalid': validationState.telephone.length > 0 })}
              placeholder="Press Enter to add phone (E.164 format: +49123456789)"
            />
            {validationState.telephone.length > 0 && (
              <small className="p-error">{validationState.telephone.toString()}</small>
            )}
            <small className="block mt-1" style={{ color: '#6c757d' }}>
              Use E.164 format: +[country code][number] (e.g., +49123456789)
            </small>
          </div>
        </div>

        {/* Company Information Section */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <h6 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Company Information
          </h6>
          
          <div className="field mb-4">
            <label htmlFor="company" className="block mb-2 font-semibold text-900">
              Company <span className="text-red-500">*</span>
            </label>
            <InputText
              id="company"
              value={customer.company}
              onKeyUp={() => onValidateCompany(customer.company)}
              onChange={(e) => onInputChange(e, 'company')}
              required
              className={classNames('w-full', { 'p-invalid': validationState.company })}
            />
            {validationState.company && <small className="p-error">{validationState.company}</small>}
          </div>

          <div className="field mb-4">
            <label htmlFor="company2" className="block mb-2 font-semibold text-900">
              Additional Company Names
            </label>
            <Chips
              id="company2"
              addOnBlur={true}
              allowDuplicate={false}
              pt={{ removeTokenIcon: { onMouseDown: (e: any) => e.preventDefault() } }}
              value={(() => {
                // Handle company2 - filter out empty strings and "[]"
                if (Array.isArray(customer.company2)) {
                  return customer.company2.filter(company => company && company.trim() !== '' && company !== '[]');
                }
                if (customer.company2 && customer.company2.trim() !== '' && customer.company2 !== '[]') {
                  return [customer.company2];
                }
                return [];
              })()}
              onAdd={(e) => onCompany2Add(e)}
              onRemove={(e) => onCompany2Remove(e)}
              onChange={(e) => onInputChange(e, 'company2')}
              separator=","
              className={classNames('w-full', { 'p-invalid': validationState.company2.length > 0 })}
              placeholder="Press Enter to add company name"
            />
            {validationState.company2.length > 0 && (
              <small className="p-error">{validationState.company2.toString()}</small>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="field mb-4">
              <label htmlFor="country" className="block mb-2 font-semibold text-900">
                Country <span className="text-red-500">*</span>
              </label>
              <Dropdown
                id="country"
                value={customer.country}
                options={countries}
                onChange={(e) => {
                  onInputChange({ target: { value: e.value } }, 'country');
                  onValidateCountry(e.value);
                }}
                optionLabel="name"
                optionValue="code"
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                placeholder="Select country"
                appendTo={document.body}
                className={classNames('w-full', { 'p-invalid': validationState.country })}
                filter
                filterPlaceholder="Search countries"
              />
              {validationState.country && <small className="p-error">{validationState.country}</small>}
            </div>

            <div className="field mb-4">
              <label htmlFor="language" className="block mb-2 font-semibold text-900">
                Language <span className="text-red-500">*</span>
              </label>
              <Dropdown
                id="language"
                value={customer.language}
                options={languages}
                onChange={(e) => {
                  onInputChange({ target: { value: e.value } }, 'language');
                  onValidateLanguage(e.value);
                }}
                optionLabel="name"
                optionValue="code"
                itemTemplate={languageOptionTemplate}
                valueTemplate={selectedLanguageTemplate}
                placeholder="Select language"
                appendTo={document.body}
                className={classNames('w-full', { 'p-invalid': validationState.language })}
                filter
                filterPlaceholder="Search languages"
              />
              {validationState.language && <small className="p-error">{validationState.language}</small>}
            </div>
          </div>
        </div>

        {/* Data Access Section */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1rem', 
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          <h6 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Data Access Permissions
          </h6>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="field-checkbox mb-4">
              <Checkbox
                inputId="pkwData"
                checked={customer.pkwData}
                onChange={(e) => onInputBooleanChange(e, 'pkwData')}
              />
              <label htmlFor="pkwData" className="ml-2 font-semibold text-900" style={{ cursor: 'pointer' }}>
                🚗 PKW Data (Passenger Cars)
              </label>
            </div>

            <div className="field-checkbox mb-4">
              <Checkbox
                inputId="lkwData"
                checked={customer.lkwData}
                onChange={(e) => onInputBooleanChange(e, 'lkwData')}
              />
              <label htmlFor="lkwData" className="ml-2 font-semibold text-900" style={{ cursor: 'pointer' }}>
                🚚 NKW Data (Commercial Vehicles)
              </label>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
